import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu, FloatingMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Image } from '@tiptap/extension-image';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { FontFamily } from '@tiptap/extension-font-family';
import { Highlight } from '@tiptap/extension-highlight';
import { Link } from '@tiptap/extension-link';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { Placeholder } from '@tiptap/extension-placeholder';
import { BubbleMenu as BubbleMenuExtension } from '@tiptap/extension-bubble-menu';
import { FloatingMenu as FloatingMenuExtension } from '@tiptap/extension-floating-menu';
import {
    Box,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Divider,
    Popover,
    Button,
    TextField,
    Typography,
    Paper,
    alpha
} from '@mui/material';
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatStrikethrough,
    FormatAlignLeft,
    FormatAlignCenter,
    FormatAlignRight,
    FormatAlignJustify,
    FormatListBulleted,
    FormatListNumbered,
    FormatQuote,
    Undo,
    Redo,
    AddLink,
    AddPhotoAlternate,
    GridOn,
    FormatColorText,
    FormatColorFill,
    Code,
    Title,
    Checklist
} from '@mui/icons-material';

interface TiptapEditorProps {
    content: string;
    onChange: (content: string) => void;
    placeholder?: string;
    minHeight?: string | number;
}

const MenuBar = ({ editor }: { editor: any }) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [linkUrl, setLinkUrl] = React.useState('');
    const [colorType, setColorType] = React.useState<'text' | 'bg'>('text');
    const [colorAnchor, setColorAnchor] = React.useState<HTMLButtonElement | null>(null);

    if (!editor) return null;

    const handleLinkOpen = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
    const handleLinkClose = () => setAnchorEl(null);
    const setLink = () => {
        if (linkUrl) {
            editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
        } else {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        }
        setLinkUrl('');
        handleLinkClose();
    };

    const colors = [
        '#000000', '#424242', '#757575', '#BDBDBD', '#F5F5F5', '#FFFFFF',
        '#D32F2F', '#C2185B', '#7B1FA2', '#512DA8', '#303F9F', '#1976D2',
        '#0288D1', '#0097A7', '#00796B', '#388E3C', '#689F38', '#AFB42B',
        '#FBC02D', '#FFA000', '#F57C00', '#E64A19', '#5D4037', '#616161'
    ];

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 0.5,
                mb: 0.5,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                backgroundColor: '#f8f9fa'
            }}
        >
            <Tooltip title="Tiêu đề">
                <ToggleButtonGroup
                    size="small"
                    value={editor.isActive('heading') ? editor.getAttributes('heading').level : 0}
                    exclusive
                    onChange={(_, level) => {
                        if (level === 0) editor.chain().focus().setParagraph().run();
                        else editor.chain().focus().toggleHeading({ level }).run();
                    }}
                >
                    <ToggleButton value={1}><Typography variant="button">H1</Typography></ToggleButton>
                    <ToggleButton value={2}><Typography variant="button">H2</Typography></ToggleButton>
                    <ToggleButton value={3}><Typography variant="button">H3</Typography></ToggleButton>
                </ToggleButtonGroup>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Tooltip title="Đậm">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    color={editor.isActive('bold') ? 'primary' : 'default'}
                >
                    <FormatBold fontSize="small" />
                </IconButton>
            </Tooltip>

            <Tooltip title="Nghiêng">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    color={editor.isActive('italic') ? 'primary' : 'default'}
                >
                    <FormatItalic fontSize="small" />
                </IconButton>
            </Tooltip>

            <Tooltip title="Gạch chân">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    color={editor.isActive('underline') ? 'primary' : 'default'}
                >
                    <FormatUnderlined fontSize="small" />
                </IconButton>
            </Tooltip>

            <Tooltip title="Gạch ngang">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    color={editor.isActive('strike') ? 'primary' : 'default'}
                >
                    <FormatStrikethrough fontSize="small" />
                </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Tooltip title="Căn lề">
                <ToggleButtonGroup
                    size="small"
                    value={editor.getAttributes('paragraph').textAlign || 'left'}
                    exclusive
                    onChange={(_, align) => editor.chain().focus().setTextAlign(align).run()}
                >
                    <ToggleButton value="left"><FormatAlignLeft fontSize="small" /></ToggleButton>
                    <ToggleButton value="center"><FormatAlignCenter fontSize="small" /></ToggleButton>
                    <ToggleButton value="right"><FormatAlignRight fontSize="small" /></ToggleButton>
                    <ToggleButton value="justify"><FormatAlignJustify fontSize="small" /></ToggleButton>
                </ToggleButtonGroup>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Tooltip title="Danh sách không phân cấp">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    color={editor.isActive('bulletList') ? 'primary' : 'default'}
                >
                    <FormatListBulleted fontSize="small" />
                </IconButton>
            </Tooltip>

            <Tooltip title="Danh sách phân cấp">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    color={editor.isActive('orderedList') ? 'primary' : 'default'}
                >
                    <FormatListNumbered fontSize="small" />
                </IconButton>
            </Tooltip>

            <Tooltip title="Danh sách công việc">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleTaskList().run()}
                    color={editor.isActive('taskList') ? 'primary' : 'default'}
                >
                    <Checklist fontSize="small" />
                </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Tooltip title="Màu chữ">
                <IconButton
                    size="small"
                    onClick={(e) => { setColorType('text'); setColorAnchor(e.currentTarget); }}
                >
                    <FormatColorText fontSize="small" style={{ color: editor.getAttributes('textStyle').color || '#000' }} />
                </IconButton>
            </Tooltip>

            <Tooltip title="Màu nền">
                <IconButton
                    size="small"
                    onClick={(e) => { setColorType('bg'); setColorAnchor(e.currentTarget); }}
                >
                    <FormatColorFill fontSize="small" style={{ color: editor.getAttributes('highlight').color || '#ccc' }} />
                </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Tooltip title="Chèn Bảng">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                >
                    <GridOn fontSize="small" />
                </IconButton>
            </Tooltip>

            <Tooltip title="Chèn Link">
                <IconButton size="small" onClick={handleLinkOpen} color={editor.isActive('link') ? 'primary' : 'default'}>
                    <AddLink fontSize="small" />
                </IconButton>
            </Tooltip>

            <Tooltip title="Chèn Ảnh">
                <IconButton
                    size="small"
                    onClick={() => {
                        const url = window.prompt('Nhập URL hình ảnh:');
                        if (url) editor.chain().focus().setImage({ src: url }).run();
                    }}
                >
                    <AddPhotoAlternate fontSize="small" />
                </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Tooltip title="Hoàn tác">
                <IconButton size="small" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
                    <Undo fontSize="small" />
                </IconButton>
            </Tooltip>

            <Tooltip title="Làm lại">
                <IconButton size="small" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
                    <Redo fontSize="small" />
                </IconButton>
            </Tooltip>

            {/* Popovers for Link and Color */}
            <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleLinkClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
                    <TextField size="small" label="URL" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} />
                    <Button variant="contained" size="small" onClick={setLink}>OK</Button>
                </Box>
            </Popover>

            <Popover open={Boolean(colorAnchor)} anchorEl={colorAnchor} onClose={() => setColorAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', width: 200, gap: 0.5 }}>
                    {colors.map((c) => (
                        <Box
                            key={c}
                            onClick={() => {
                                if (colorType === 'text') editor.chain().focus().setColor(c).run();
                                else editor.chain().focus().toggleHighlight({ color: c }).run();
                                setColorAnchor(null);
                            }}
                            sx={{ width: 24, height: 24, backgroundColor: c, cursor: 'pointer', border: '1px solid #ddd', '&:hover': { border: '1px solid #333' } }}
                        />
                    ))}
                    <Button fullWidth size="small" onClick={() => {
                        if (colorType === 'text') editor.chain().focus().unsetColor().run();
                        else editor.chain().focus().unsetHighlight().run();
                        setColorAnchor(null);
                    }}>Xóa màu</Button>
                </Box>
            </Popover>
        </Paper>
    );
};

export const TiptapEditor: React.FC<TiptapEditorProps> = ({
    content,
    onChange,
    placeholder = 'Bắt đầu soạn thảo...',
    minHeight = 300
}) => {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Table.configure({ resizable: true }),
            TableRow,
            TableCell,
            TableHeader,
            Image,
            Link.configure({ openOnClick: false }),
            TextStyle,
            Color,
            FontFamily,
            Highlight.configure({ multicolor: true }),
            TaskList,
            TaskItem.configure({ nested: true }),
            Placeholder.configure({ placeholder }),
            BubbleMenuExtension,
            FloatingMenuExtension
        ],
        content: content,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
    });

    // Keep content synced if changed from outside
    React.useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content);
        }
    }, [content, editor]);

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                '&:focus-within': {
                    borderColor: 'primary.main',
                    boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`
                },
                '& .ProseMirror': {
                    minHeight: minHeight,
                    p: 2,
                    outline: 'none',
                    '& p.is-editor-empty:first-of-type::before': {
                        content: 'attr(data-placeholder)',
                        float: 'left',
                        color: 'text.disabled',
                        pointerEvents: 'none',
                        height: 0,
                    },
                    '& table': {
                        borderCollapse: 'collapse',
                        tableLayout: 'fixed',
                        width: '100%',
                        margin: 0,
                        overflow: 'hidden',
                        '& td, & th': {
                            minWidth: '1em',
                            border: '1px solid',
                            borderColor: 'divider',
                            padding: '3px 5px',
                            verticalAlign: 'top',
                            boxSizing: 'border-box',
                            position: 'relative',
                            '& > *': { mb: 0 }
                        },
                        '& th': {
                            fontWeight: 'bold',
                            textAlign: 'left',
                            backgroundColor: (theme) => alpha(theme.palette.action.selected, 0.05)
                        }
                    },
                    '& ul, & ol': {
                        paddingLeft: '1.2rem',
                    },
                    '& ul[data-type="taskList"]': {
                        listStyle: 'none',
                        padding: 0,
                        '& li': {
                            display: 'flex',
                            '& > label': {
                                flex: '0 0 auto',
                                marginRight: '0.5rem',
                                userSelect: 'none',
                            },
                            '& > div': {
                                flex: '1 1 auto',
                            }
                        }
                    },
                    '& blockquote': {
                        borderLeft: '3px solid',
                        borderColor: 'divider',
                        paddingLeft: '1rem',
                        margin: '1rem 0',
                        color: 'text.secondary'
                    },
                    '& img': {
                        maxWidth: '100%',
                        height: 'auto',
                    }
                }
            }}
        >
            <MenuBar editor={editor} />
            <EditorContent editor={editor} />

            {/* Dynamic Table Bubble Menu */}
            {editor && editor.isActive('table') && (
                <BubbleMenu editor={editor}>
                    <Paper elevation={3} sx={{ p: 0.5, display: 'flex', gap: 0.5, backgroundColor: '#333' }}>
                        <Button size="small" variant="text" sx={{ color: '#fff', minWidth: 0, px: 1 }} onClick={() => editor.chain().focus().addColumnBefore().run()}>+ Cột Trái</Button>
                        <Button size="small" variant="text" sx={{ color: '#fff', minWidth: 0, px: 1 }} onClick={() => editor.chain().focus().addColumnAfter().run()}>+ Cột Phải</Button>
                        <Button size="small" variant="text" sx={{ color: '#fff', minWidth: 0, px: 1 }} onClick={() => editor.chain().focus().addRowAfter().run()}>+ Dòng Dưới</Button>
                        <Button size="small" variant="text" sx={{ color: '#fff', minWidth: 0, px: 1 }} onClick={() => editor.chain().focus().deleteTable().run()}>Xóa Bảng</Button>
                    </Paper>
                </BubbleMenu>
            )}
        </Box>
    );
};
